/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import React, { createContext, useContext, useEffect, useReducer, useMemo } from "react";

import { dispatchGlobalNotification } from "../utils/notification";

const MaterialUI = createContext({});

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

const SIDENAV_PINNED_STORAGE_KEY = "beas.sidenav.pinned";
const THEME_PREFERENCE_STORAGE_KEY = "beas.theme.preference";

export type ThemePreference = "light" | "dark" | "system";

function isThemePreference(value: unknown): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

function readSystemDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolveDarkMode(preference: ThemePreference): boolean {
  if (preference === "system") return readSystemDark();
  return preference === "dark";
}

function readThemePreferenceFromStorage(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

function writeThemePreferenceToStorage(preference: ThemePreference) {
  try {
    window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
  } catch {
    /* ignore */
  }
}

function readSidenavPinnedFromStorage(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIDENAV_PINNED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSidenavPinnedToStorage(pinned: boolean) {
  try {
    if (pinned) window.localStorage.setItem(SIDENAV_PINNED_STORAGE_KEY, "1");
    else window.localStorage.removeItem(SIDENAV_PINNED_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function getInitialMaterialUIState() {
  const pinned = readSidenavPinnedFromStorage();
  const themePreference = readThemePreferenceFromStorage();
  return {
    miniSidenav: !pinned,
    sidenavPinned: pinned,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: false,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    themePreference,
    darkMode: resolveDarkMode(themePreference),
  };
}

function reducer(state: any, action: any) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "SIDENAV_PINNED": {
      return {
        ...state,
        sidenavPinned: action.value,
        miniSidenav: action.value ? false : true,
      };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "THEME_PREFERENCE": {
      const themePreference = isThemePreference(action.value) ? action.value : "system";
      return {
        ...state,
        themePreference,
        darkMode: resolveDarkMode(themePreference),
      };
    }
    case "DARKMODE": {
      return { ...state, darkMode: Boolean(action.value) };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function MaterialUIControllerProvider({ children }: { children: React.ReactNode }) {

  const [controller, dispatch] = useReducer(reducer, null, getInitialMaterialUIState);

  useEffect(() => {
    if (controller.themePreference !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => dispatch({ type: "DARKMODE", value: media.matches });
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [controller.themePreference]);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

function useMaterialUIController(): [any, any] {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context as [any, any];
}

// Context module functions
const setMiniSidenav = (dispatch: any, value: any) => dispatch({ type: "MINI_SIDENAV", value });
const setSidenavPinned = (dispatch: any, value: boolean) => {
  writeSidenavPinnedToStorage(value);
  dispatch({ type: "SIDENAV_PINNED", value });
};
const setTransparentSidenav = (dispatch: any, value: any) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch: any, value: any) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch: any, value: any) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch: any, value: any) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch: any, value: any) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch: any, value: any) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch: any, value: any) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch: any, value: any) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch: any, value: any) => dispatch({ type: "DARKMODE", value });
const setThemePreference = (dispatch: any, value: ThemePreference) => {
  writeThemePreferenceToStorage(value);
  dispatch({ type: "THEME_PREFERENCE", value });
};
const showMessage = (message: unknown) => {
  const content =
    typeof message === "string"
      ? message
      : message != null
        ? String(message)
        : "";
  dispatchGlobalNotification({
    color: "info",
    icon: "notifications",
    title: "Notice",
    reason: "App",
    content,
    info: "",
  });
};
export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setSidenavPinned,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setThemePreference,
  showMessage
};
